import DeleteIcon from '@mui/icons-material/Delete'; // Icon for delete button
import { Box, Button, IconButton } from '@mui/material'; // Material-UI components
import { H1, P } from 'baseline-ui/typography'; // Typography components
import useProcessLoader from 'common/hooks/useProcessLoader'; // Custom hook for loading states
import { Formik } from 'formik'; // Form management library
import React, { useEffect, useRef } from 'react'; // React core utilities
import { FormattedMessage } from 'react-intl'; // Internationalization utility
import { useDispatch, useSelector } from 'react-redux'; // Redux hooks for state management
import { SET_BUSINESS_FORM_DATA_START } from 'stores/redux/actions/BusinessFormActions'; // Redux action for setting form data
import { ADD_BUSINESS_CATEGORY_STACK_START, DELETE_BUSINESS_CATEGORY_STACK_START, GET_BUSINESS_CATEGORIES_INITIALIZE_START, GET_BUSINESS_CATEGORIES_PROCESS, GET_PRODUCT_BUSINESS_CATEGORIES_STACK_INITIALIZE_PROCESS } from 'stores/redux/actions/CategoriesActions'; // Redux actions for category management
import { businessFormSelector } from 'stores/redux/selectors/businessFormSelectors'; // Redux selector for business form data
import ProductCategoriesFormFields from './ProductCategoriesForm/ProductCategoriesFormFields'; // Custom form fields component

// Main component definition
const StackedBusinessCategories = () => {
    // State to manage selected filters for categories
    const [selectedFilters, setSelectedFilters] = React.useState({});
    const dispatch = useDispatch(); // Redux dispatch function
    const { type } = useSelector(businessFormSelector); // Selector for business form type

    // Selector for the stack of business categories
    const businessCategoriesStack = useSelector(({ CategoriesReducer }) => CategoriesReducer.businessCategoriesStack);

    // Selector for selected categories, with fallback to mapping or selectedCategories
    const selectedCategories = useSelector(({ BusinessFormReducer }) => {
        const mapping = BusinessFormReducer.mapping?.tags || [];
        const selectedCategories = BusinessFormReducer.selectedCategories || [];

        if (mapping.length) {
            return mapping;
        }

        return selectedCategories;
    });

    const allValues = useRef({}); // Ref to store form data for all categories

    // Hook to check if the process for initializing the product categories stack is loading
    const isLoadingStart = useProcessLoader({
        processNames: [GET_PRODUCT_BUSINESS_CATEGORIES_STACK_INITIALIZE_PROCESS],
    });

    // Hook to check if the process for fetching business categories is loading
    const isLoadingCategories = useProcessLoader({
        processNames: [GET_BUSINESS_CATEGORIES_PROCESS],
    });

    // Effect to initialize categories and filters when the component mounts or dependencies change
    useEffect(() => {
        if (!selectedCategories.length) {
            addNewCategory(); // Add a new category if none are selected
            return;
        }

        let filters = {};
        selectedCategories
            .filter((category) => category.type === 'category') // Filter for categories
            .forEach((category) => {
                filters[category.id] = selectedCategories
                    .filter((filter) => filter.type === 'filter' && filter.crumbs.some(crumb => crumb._id === category.id)) // Match filters to categories
                    .map((filter) => filter.id);
            });
        setSelectedFilters(filters); // Update state with selected filters

        // Dispatch action to initialize categories with selected data
        dispatch({ type: GET_BUSINESS_CATEGORIES_INITIALIZE_START, payload: { selectedCategories } });

    }, [isLoadingCategories]); // Dependency array includes loading state for categories

    // Function to handle form submission for a specific category
    const onSubmit = (data, index) => {
        const oldData = Object.values(allValues.current).flat(); // Flatten existing form data

        if (data) {
            allValues.current[index] = data; // Update form data for the given index
        } else {
            delete allValues.current[index]; // Remove form data for the given index
        }

        const mergedData = Object.values(allValues.current).flat(); // Flatten updated form data

        let areDifferent = oldData.length !== mergedData.length; // Check if data length has changed

        if (!areDifferent) {
            for (let index = 0; index < oldData.length; index++) {
                if (oldData[index].id !== mergedData[index].id) {
                    areDifferent = true; // Check if data content has changed
                }
            }

            if (!areDifferent) {
                return; // Exit if no changes detected
            }
        }

        // Dispatch action to update form data in Redux store
        dispatch({
            type: SET_BUSINESS_FORM_DATA_START,
            payload: {
                selectedCategories: mergedData,
                hasUnsavedChanges: true,
            },
        });
    };

    // Function to add a new category to the stack
    const addNewCategory = () => {
        dispatch({
            type: ADD_BUSINESS_CATEGORY_STACK_START,
            payload: {
                type, // Pass the current business form type
            },
        });
    };

    // Function to delete a category from the stack
    const deleteCategory = (index) => {
        onSubmit(undefined, index); // Clear form data for the given index
        dispatch({
            type: DELETE_BUSINESS_CATEGORY_STACK_START,
            payload: {
                index, // Pass the index of the category to delete
            },
        });
    };

    // Function to update selected filters for a specific category
    const onSelectedFiltersChange = (filters, categoryId) => {
        setSelectedFilters((old) => ({ ...old, [categoryId]: filters })); // Update state with new filters
    };

    // Main component rendering
    return (
        <>
            {/* Title and subtitle */}
            <H1 color="primary">
                <FormattedMessage id="addBusiness.page.sections.categories.title" />
            </H1>
            <P>
                <FormattedMessage id={`${type}.page.sections.categories.subtitle`} />
            </P>
            {isLoadingStart && <Box mb={3} display="flex" alignItems="center">
                <Box sx={{ width: '-webkit-fill-available' }}>
                    <P>
                        <FormattedMessage id={"addBusiness.page.sections.categories.loading"} />
                    </P>
                </Box>
            </Box>}
            {/* Render each category stack */}
            {!isLoadingStart && businessCategoriesStack.map((stack, index) => {
                const categoryId = stack[1]?.categoryId; // Get category ID from stack
                const filters = selectedFilters?.[categoryId] || []; // Get filters for the category

                return (
                    <Box key={`product-category-${index}`} mb={3} display="flex" alignItems="center">
                        <Box sx={{ width: '-webkit-fill-available' }}>
                            <Formik>
                                <ProductCategoriesFormFields
                                    selectedFilters={filters} // Pass selected filters
                                    categoryFormIndex={index} // Pass form index
                                    onSelectedFiltersChange={(filters) => onSelectedFiltersChange(filters, categoryId)} // Handle filter changes
                                    onSubmit={(data) => onSubmit(data, index)} // Handle form submission
                                />
                            </Formik>
                        </Box>
                        {businessCategoriesStack?.length > 1 && (
                            <IconButton
                                aria-label="delete"
                                onClick={() => deleteCategory(index)} // Handle category deletion
                                sx={{ marginLeft: 2 }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>
                );
            })}

            {/* Add new category button */}
            <Box display="flex" justifyContent="space-between" gap={2}>
                <Button
                    type="button"
                    variant="contained"
                    color="warning"
                    sx={{ borderRadius: '25px' }}
                    onClick={addNewCategory} // Handle adding a new category
                >
                    Add New Category
                </Button>
            </Box>
        </>
    );
};

export default StackedBusinessCategories; // Export the component
